import { useEffect, useRef } from 'react';
import { useRunningWebLock, useSignalR } from '@s20/hooks/signalr.hooks';
import { parseBroadcastMeta, reportHubLifecycle, setBoardHubConnection } from './boardTelemetry';

const onLifecycle = (event, info) => reportHubLifecycle('legacy', event, info);

/**
 * Puts this connection in the groups for what the board shows; the server sends the board nothing else. If that
 * fails the board falls back to the group every dispatch update goes to, so it is never left without updates.
 */
const subscribeToBoard = async (hub, subscription) => {
    try {
        await hub.invoke('SubscribeToBoard', subscription);
    } catch (e) {
        reportHubLifecycle('legacy', 'subscribe-failed', { connectionId: hub.connectionId, error: e });
        await hub.invoke('JoinGroup', 'Dispatch').catch(() => undefined);
    }
};

const MAX_SEEN_BROADCASTS = 200;

/**
 * A message sent to several groups arrives once per group the board is in (its office and a job it shows from another
 * office), so a broadcast already handled is skipped.
 */
const isRepeat = (seenBroadcasts, meta) => {
    const broadcastId = parseBroadcastMeta(meta)?.broadcastId;

    if (!broadcastId) return false;
    if (seenBroadcasts.has(broadcastId)) return true;

    seenBroadcasts.add(broadcastId);
    if (seenBroadcasts.size > MAX_SEEN_BROADCASTS) {
        seenBroadcasts.delete(seenBroadcasts.values().next().value);
    }

    return false;
};

/** Registers a hub message handler that ignores repeats of a broadcast. */
const onOnce = (hub, seenBroadcasts, method, handler) => hub.on(method, (json, meta) => {
    if (!isRepeat(seenBroadcasts, meta)) handler(json, meta);
});

export const DispatchHub = ({
    onJobCreated,
    onJobUpdated,
    onSendMultipleOperations,
    onJobCancellationUpdated,
    onJobsUpdatedBulk,
    onJobDeleted,
    onStaffRelatedDataUpdated,
    onReconnect,
    subscription,
}) => {
    const hub = useSignalR('legacyDispatchHub', { onReconnect, onLifecycle });
    useRunningWebLock('legacyDispatchHub');
    const seenBroadcastsRef = useRef(new Set());

    useEffect(() => {
        if (hub) {
            onOnce(hub, seenBroadcastsRef.current, 'JobCreated', (json, meta) => {
                onJobCreated(json, undefined, meta);
            });

            onOnce(hub, seenBroadcastsRef.current, 'JobUpdated', (json, meta) => {
                onJobUpdated(json, meta);
            });

            onOnce(hub, seenBroadcastsRef.current, 'SendMultipleOperations', (json, meta) => {
                onSendMultipleOperations(json, meta);
            });

            onOnce(hub, seenBroadcastsRef.current, 'JobCancellationUpdated', (json, meta) => {
                onJobCancellationUpdated(json, meta);
            });

            onOnce(hub, seenBroadcastsRef.current, 'JobsUpdatedBulk', (json, meta) => {
                onJobsUpdatedBulk(json, undefined, meta);
            });

            onOnce(hub, seenBroadcastsRef.current, 'JobDeleted', (json, meta) => {
                onJobDeleted(json, undefined, meta);
            });

            hub.on('StaffRelatedDataUpdated', (json) => {
                onStaffRelatedDataUpdated(json);
            });

            setBoardHubConnection('legacy', hub.connectionId);

            return () => {
                hub.off('JobCreated');
                hub.off('JobUpdated');
                hub.off('SendMultipleOperations');
                hub.off('JobCancellationUpdated');
                hub.off('JobsUpdatedBulk');
                hub.off('JobDeleted');
                hub.off('StaffRelatedDataUpdated');
            };
        }

        return undefined;
    }, [hub, onJobCancellationUpdated, onJobCreated, onJobDeleted, onJobUpdated, onJobsUpdatedBulk, onReconnect, onSendMultipleOperations, onStaffRelatedDataUpdated]);

    const subscriptionKey = JSON.stringify(subscription);

    useEffect(() => {
        if (hub) {
            void subscribeToBoard(hub, JSON.parse(subscriptionKey));
        }
    }, [hub, subscriptionKey]);

    return null;
};